import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiCheckCircle,
  FiClock,
  FiEye,
  FiHash,
  FiLock,
  FiMail,
  FiMessageCircle,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUser,
  FiUsers
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { accountAPI, adminToolsAPI } from '../utils/api';

const roleLabels = {
  system: 'System',
  super_admin: 'Super Admin',
  reseller: 'Reseller',
  user: 'User'
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const roleBadge = (role) => {
  const map = {
    system: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    super_admin: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    reseller: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
    user: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
  };
  return map[role] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
};

const statusBadge = (active) => (
  active
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
);

const HeroCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-[24px] border border-white/15 bg-white/10 p-4 backdrop-blur">
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white">
      <Icon className="h-5 w-5" />
    </div>
    <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">{label}</p>
    <p className="mt-2 text-2xl font-black text-white">{value}</p>
  </div>
);

const WorkspacePanel = ({ title, description, icon: Icon, tint, children }) => (
  <section className="rounded-[28px] border border-white/80 bg-white p-5 shadow-[0_24px_60px_rgba(15,23,42,0.08)] dark:border-gray-800 dark:bg-gray-900">
    <div className="flex items-start gap-4">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
        <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">{description}</p>
      </div>
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

const SearchBox = ({ value, onChange, placeholder }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
    <FiSearch className="h-4 w-4 text-gray-400" />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full border-0 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-gray-500"
    />
  </div>
);

const EmptyState = ({ title, body }) => (
  <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center dark:border-gray-700 dark:bg-gray-800/60">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
      <FiSearch className="h-6 w-6" />
    </div>
    <p className="mt-4 text-lg font-bold text-gray-900 dark:text-white">{title}</p>
    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{body}</p>
  </div>
);

const UserRowCard = ({ user, active, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(user)}
    className={`w-full rounded-[24px] border p-4 text-left transition ${
      active
        ? 'border-rose-200 bg-rose-50 shadow-sm dark:border-rose-900/40 dark:bg-rose-950/20'
        : 'border-slate-200 bg-white hover:border-rose-200 hover:bg-rose-50/60 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-rose-900/30 dark:hover:bg-rose-950/10'
    }`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-base font-bold text-gray-900 dark:text-white">{user.account_name}</p>
        <div className="mt-1 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <FiMail className="h-4 w-4" />
          <span>{user.email}</span>
        </div>
      </div>
      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${roleBadge(user.role)}`}>
        {roleLabels[user.role] || user.role}
      </span>
    </div>
    <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
      <span>ID {user.id}</span>
      <span>{formatDate(user.created_at)}</span>
    </div>
  </button>
);

const ChatRowCard = ({ chat, active, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(chat)}
    className={`w-full rounded-[24px] border p-4 text-left transition ${
      active
        ? 'border-amber-200 bg-amber-50 shadow-sm dark:border-amber-900/40 dark:bg-amber-950/20'
        : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/60 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-amber-900/30 dark:hover:bg-amber-950/10'
    }`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-base font-bold text-gray-900 dark:text-white">{chat.title || 'Untitled Chat'}</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Chat ID: {chat.chat_id}</p>
      </div>
      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadge(chat.is_active)}`}>
        {chat.is_active ? 'Active' : 'Inactive'}
      </span>
    </div>
    <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
      <span>{chat.requester_name || '-'}</span>
      <span>{formatDate(chat.created_at)}</span>
    </div>
  </button>
);

const DetailTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-slate-50 p-4 dark:bg-gray-800/70">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </div>
    <p className="mt-2 break-words text-sm font-semibold leading-6 text-gray-900 dark:text-white">{value || '-'}</p>
  </div>
);

const LoadMoreButton = ({ loading, onClick, label, tone }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-70 ${
      tone === 'rose'
        ? 'border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/30 dark:bg-rose-950/20 dark:text-rose-300'
        : 'border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/30 dark:bg-amber-950/20 dark:text-amber-300'
    }`}
  >
    {loading ? <FiRefreshCw className="h-4 w-4 animate-spin" /> : <FiEye className="h-4 w-4" />}
    {label}
  </button>
);

const AdminTools = () => {
  const [accountRole, setAccountRole] = useState('user');
  const [loading, setLoading] = useState({
    users: false,
    userDetail: false,
    userMore: false,
    chats: false,
    chatDetail: false,
    chatMore: false,
    reset: false
  });
  const [users, setUsers] = useState({ items: [], page: 0, pageSize: 10, hasMore: false });
  const [chats, setChats] = useState({ items: [], page: 0, pageSize: 10, hasMore: false });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserChats, setSelectedUserChats] = useState([]);
  const [selectedUserMeta, setSelectedUserMeta] = useState({ page: 0, pageSize: 10, hasMore: false });
  const [selectedChat, setSelectedChat] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [chatSearch, setChatSearch] = useState('');
  const [passwordForm, setPasswordForm] = useState({ target: '', new_password: '' });
  const [resetMessage, setResetMessage] = useState('');

  const isSystemOrSuperAdmin = ['system', 'super_admin'].includes(accountRole);
  const canManageChats = ['system', 'super_admin', 'reseller'].includes(accountRole);

  const setLoadingState = (key, value) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  };

  const loadUsers = async (page = 1, append = false) => {
    try {
      setLoadingState(append ? 'userMore' : 'users', true);
      const response = await adminToolsAPI.listUsers({ page, page_size: users.pageSize || 10 });
      const payload = response.data;
      const rows = payload.data || [];
      setUsers((prev) => ({
        items: append ? [...prev.items, ...rows] : rows,
        page: payload.page || page,
        pageSize: payload.page_size || prev.pageSize || 10,
        hasMore: Boolean(payload.has_more)
      }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load user accounts');
    } finally {
      setLoadingState(append ? 'userMore' : 'users', false);
    }
  };

  const loadChats = async (page = 1, append = false) => {
    try {
      setLoadingState(append ? 'chatMore' : 'chats', true);
      const response = await adminToolsAPI.listChats({ page, page_size: chats.pageSize || 10 });
      const payload = response.data;
      const rows = payload.data || [];
      setChats((prev) => ({
        items: append ? [...prev.items, ...rows] : rows,
        page: payload.page || page,
        pageSize: payload.page_size || prev.pageSize || 10,
        hasMore: Boolean(payload.has_more)
      }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load requested chats');
    } finally {
      setLoadingState(append ? 'chatMore' : 'chats', false);
    }
  };

  const openUser = async (user) => {
    try {
      setLoadingState('userDetail', true);
      const response = await adminToolsAPI.getUserDetail(user.id, { page: 1, page_size: selectedUserMeta.pageSize || 10 });
      const payload = response.data;
      setSelectedUser(payload.data?.user || user);
      setSelectedUserChats(payload.data?.chats || []);
      setSelectedUserMeta({
        page: payload.page || 1,
        pageSize: payload.page_size || 10,
        hasMore: Boolean(payload.has_more)
      });
      setPasswordForm((prev) => ({ ...prev, target: String((payload.data?.user || user).id || '') }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load account details');
    } finally {
      setLoadingState('userDetail', false);
    }
  };

  const loadMoreSelectedUserChats = async () => {
    if (!selectedUser) return;
    try {
      setLoadingState('userMore', true);
      const nextPage = selectedUserMeta.page + 1;
      const response = await adminToolsAPI.getUserDetail(selectedUser.id, { page: nextPage, page_size: selectedUserMeta.pageSize || 10 });
      const payload = response.data;
      setSelectedUserChats((prev) => [...prev, ...(payload.data?.chats || [])]);
      setSelectedUserMeta((prev) => ({
        ...prev,
        page: payload.page || nextPage,
        pageSize: payload.page_size || prev.pageSize,
        hasMore: Boolean(payload.has_more)
      }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load more chats');
    } finally {
      setLoadingState('userMore', false);
    }
  };

  const openChat = async (chat) => {
    try {
      setLoadingState('chatDetail', true);
      const response = await adminToolsAPI.getChatDetail(chat.chat_id);
      setSelectedChat(response.data?.data || chat);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load chat detail');
    } finally {
      setLoadingState('chatDetail', false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordForm.target.trim() || !passwordForm.new_password.trim()) {
      toast.error('Child account and new password are required');
      return;
    }

    try {
      setLoadingState('reset', true);
      const response = await accountAPI.resetChildPassword(passwordForm);
      setResetMessage(response.data?.message || 'Child password changed successfully');
      setPasswordForm((prev) => ({ ...prev, new_password: '' }));
      toast.success(response.data?.message || 'Child password changed');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not change child password');
    } finally {
      setLoadingState('reset', false);
    }
  };

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const response = await accountAPI.getMe();
        const role = response.data?.data?.role || 'user';
        setAccountRole(role);

        if (['system', 'super_admin'].includes(role)) {
          loadUsers();
        }
        if (['system', 'super_admin', 'reseller'].includes(role)) {
          loadChats();
        }
      } catch (error) {
        console.error('Error loading admin tool access:', error);
        toast.error('Failed to load admin tool access');
      }
    };

    fetchAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    if (!query) return users.items;
    return users.items.filter((user) =>
      [user.account_name, user.email, user.id].join(' ').toLowerCase().includes(query)
    );
  }, [userSearch, users.items]);

  const filteredChats = useMemo(() => {
    const query = chatSearch.trim().toLowerCase();
    if (!query) return chats.items;
    return chats.items.filter((chat) =>
      [chat.title, chat.chat_id, chat.requester_name, chat.approver_name].join(' ').toLowerCase().includes(query)
    );
  }, [chatSearch, chats.items]);

  if (!canManageChats && !isSystemOrSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <EmptyState
            title="Admin Tool access is not available"
            body="This page is only available for reseller, super admin, and system accounts."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-[30px] border border-rose-100 bg-gradient-to-br from-rose-600 via-red-500 to-orange-500 p-6 text-white shadow-[0_30px_80px_rgba(225,29,72,0.24)] dark:border-rose-500/20"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.14),transparent_30%)]" />
          <div className="relative grid gap-6 lg:grid-cols-[1.3fr,1fr]">
            <div className="space-y-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-rose-50">
                <FiShield className="h-4 w-4" />
                Admin Tool
              </span>
              <div className="space-y-3">
                <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Choose from the list, see the detail on the right</h1>
                <p className="max-w-3xl text-sm leading-7 text-rose-50/90 sm:text-base">
                  This page is built for quick support work. Pick an account or chat from the list, then review the important information in a clear detail panel.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <HeroCard icon={FiUser} label="Role" value={roleLabels[accountRole] || 'Account'} />
              <HeroCard icon={FiUsers} label="Accounts" value={isSystemOrSuperAdmin ? users.items.length : '-'} />
              <HeroCard icon={FiMessageCircle} label="Chats" value={canManageChats ? chats.items.length : '-'} />
            </div>
          </div>
        </motion.section>

        <div className="mt-8 space-y-8">
          {isSystemOrSuperAdmin && (
            <WorkspacePanel
              title="Account Workspace"
              description="Search an account, tap it once, and the full account detail with owned chats will appear next to the list."
              icon={FiUsers}
              tint="bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300"
            >
              <div className="grid gap-5 xl:grid-cols-[0.95fr,1.05fr]">
                <div className="space-y-4">
                  <SearchBox
                    value={userSearch}
                    onChange={(event) => setUserSearch(event.target.value)}
                    placeholder="Search by account name, email, or user ID"
                  />
                  <div className="space-y-3">
                    {loading.users && !users.items.length ? (
                      <EmptyState title="Loading accounts" body="Please wait while account data is being prepared." />
                    ) : filteredUsers.length ? (
                      filteredUsers.map((user) => (
                        <UserRowCard
                          key={user.id}
                          user={user}
                          active={selectedUser?.id === user.id}
                          onOpen={openUser}
                        />
                      ))
                    ) : (
                      <EmptyState title="No matching accounts" body="Try another keyword or wait for more accounts to load." />
                    )}
                  </div>
                  {users.hasMore && (
                    <div className="flex justify-center">
                      <LoadMoreButton
                        tone="rose"
                        loading={loading.userMore}
                        onClick={() => loadUsers(users.page + 1, true)}
                        label="Load More Accounts"
                      />
                    </div>
                  )}
                </div>

                <div className="rounded-[26px] border border-slate-200 bg-slate-50 p-5 dark:border-gray-800 dark:bg-gray-950/40">
                  {selectedUser ? (
                    <div className="space-y-5">
                      <div className="rounded-[24px] bg-white p-5 shadow-sm dark:bg-gray-900">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p className="text-2xl font-black text-gray-900 dark:text-white">{selectedUser.account_name}</p>
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{selectedUser.email}</p>
                          </div>
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${roleBadge(selectedUser.role)}`}>
                            {roleLabels[selectedUser.role] || selectedUser.role}
                          </span>
                        </div>
                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                          <DetailTile icon={FiHash} label="User ID" value={selectedUser.id} />
                          <DetailTile icon={FiShield} label="Admin ID" value={selectedUser.admin_id} />
                          <DetailTile icon={FiUser} label="Telegram ID" value={selectedUser.telegram_id} />
                          <DetailTile icon={FiClock} label="Created" value={formatDate(selectedUser.created_at)} />
                        </div>
                      </div>

                      <div className="rounded-[24px] bg-white p-5 shadow-sm dark:bg-gray-900">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Owned Chats</h3>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">These chats belong to the selected account.</p>
                          </div>
                          {loading.userDetail && <FiRefreshCw className="h-5 w-5 animate-spin text-rose-500" />}
                        </div>
                        <div className="mt-4 space-y-3">
                          {selectedUserChats.length ? selectedUserChats.map((chat) => (
                            <ChatRowCard
                              key={`${selectedUser.id}-${chat.id}`}
                              chat={chat}
                              active={selectedChat?.chat_id === chat.chat_id}
                              onOpen={openChat}
                            />
                          )) : (
                            <EmptyState title="No owned chats" body="This account does not have any chat in the current response." />
                          )}
                        </div>
                        {selectedUserMeta.hasMore && (
                          <div className="mt-4 flex justify-center">
                            <LoadMoreButton
                              tone="rose"
                              loading={loading.userMore}
                              onClick={loadMoreSelectedUserChats}
                              label="Load More Owned Chats"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <EmptyState title="Pick an account from the left" body="The selected account information will appear here together with its owned chats." />
                  )}
                </div>
              </div>
            </WorkspacePanel>
          )}

          {canManageChats && (
            <WorkspacePanel
              title="Chat Workspace"
              description="Search chats on the left. When you open one chat, the requester and approver information will appear on the right."
              icon={FiMessageCircle}
              tint="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300"
            >
              <div className="grid gap-5 xl:grid-cols-[0.95fr,1.05fr]">
                <div className="space-y-4">
                  <SearchBox
                    value={chatSearch}
                    onChange={(event) => setChatSearch(event.target.value)}
                    placeholder="Search by title, chat ID, requester, or approver"
                  />
                  <div className="space-y-3">
                    {loading.chats && !chats.items.length ? (
                      <EmptyState title="Loading chats" body="Please wait while requested chat data is being prepared." />
                    ) : filteredChats.length ? (
                      filteredChats.map((chat) => (
                        <ChatRowCard
                          key={chat.id}
                          chat={chat}
                          active={selectedChat?.chat_id === chat.chat_id}
                          onOpen={openChat}
                        />
                      ))
                    ) : (
                      <EmptyState title="No matching chats" body="Try another keyword or wait for more chats to load." />
                    )}
                  </div>
                  {chats.hasMore && (
                    <div className="flex justify-center">
                      <LoadMoreButton
                        tone="amber"
                        loading={loading.chatMore}
                        onClick={() => loadChats(chats.page + 1, true)}
                        label="Load More Chats"
                      />
                    </div>
                  )}
                </div>

                <div className="rounded-[26px] border border-slate-200 bg-slate-50 p-5 dark:border-gray-800 dark:bg-gray-950/40">
                  {selectedChat ? (
                    <div className="space-y-5">
                      <div className="rounded-[24px] bg-white p-5 shadow-sm dark:bg-gray-900">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p className="text-2xl font-black text-gray-900 dark:text-white">{selectedChat.title || 'Untitled Chat'}</p>
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Chat ID: {selectedChat.chat_id}</p>
                          </div>
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadge(selectedChat.is_active)}`}>
                            {selectedChat.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                          <DetailTile icon={FiMessageCircle} label="Chat Type" value={selectedChat.chat_type} />
                          <DetailTile icon={FiClock} label="Created" value={formatDate(selectedChat.created_at)} />
                        </div>
                      </div>

                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className="rounded-[24px] bg-white p-5 shadow-sm dark:bg-gray-900">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Requester</h3>
                          <div className="mt-4 grid gap-3">
                            <DetailTile icon={FiUser} label="Name" value={selectedChat.requester_name} />
                            <DetailTile icon={FiMail} label="Email" value={selectedChat.requester_email} />
                            <DetailTile icon={FiHash} label="User ID" value={selectedChat.request_by} />
                          </div>
                        </div>
                        <div className="rounded-[24px] bg-white p-5 shadow-sm dark:bg-gray-900">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Approver</h3>
                          <div className="mt-4 grid gap-3">
                            <DetailTile icon={FiShield} label="Name" value={selectedChat.approver_name} />
                            <DetailTile icon={FiMail} label="Email" value={selectedChat.approver_email} />
                            <DetailTile icon={FiHash} label="User ID" value={selectedChat.approved_by} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <EmptyState title="Pick a chat from the left" body="The selected chat detail will appear here with requester and approver information." />
                  )}
                </div>
              </div>
            </WorkspacePanel>
          )}

          {isSystemOrSuperAdmin && (
            <WorkspacePanel
              title="Password Help"
              description="Use this quick support form when a child account needs a new password. Opening an account above will fill in the user ID for you."
              icon={FiLock}
              tint="bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300"
            >
              <div className="grid gap-5 xl:grid-cols-[1fr,0.95fr]">
                <div className="grid gap-4 md:grid-cols-[1fr,1fr,auto]">
                  <label className="space-y-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Child User ID or Email</span>
                    <input
                      type="text"
                      value={passwordForm.target}
                      onChange={(event) => setPasswordForm((prev) => ({ ...prev, target: event.target.value }))}
                      placeholder="example@gmail.com or 55"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-900 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">New Password</span>
                    <input
                      type="text"
                      value={passwordForm.new_password}
                      onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))}
                      placeholder="Enter new password"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-900 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                  </label>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={loading.reset}
                      className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-rose-500 to-red-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-rose-500/20 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {loading.reset ? <FiRefreshCw className="h-4 w-4 animate-spin" /> : <FiCheckCircle className="h-4 w-4" />}
                      Change Password
                    </button>
                  </div>
                </div>

                <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                  <p className="text-lg font-bold text-emerald-900 dark:text-emerald-200">
                    {resetMessage || 'Ready to help a child account'}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-emerald-800 dark:text-emerald-300">
                    Choose an account from the workspace above or type the child email manually, then set the new password here.
                  </p>
                </div>
              </div>
            </WorkspacePanel>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTools;
